import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import { LoadingSpinner, TabMenu, InlineAlert } from '../../../../components/BaseComponents'
import PageHeadings from '../../../../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../../../../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../../../../components/Router/routes'
import { getSupplier } from '../../../../redux/actions/suppliersActions'
import { getSupplierPeriodStatistics } from '../../../../redux/actions/supplierStatisticsActions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks/reduxHooks'
import {
	selectIndividualSupplier,
	selectSuppliersStatus,
	SUPPLIERS_RESET,
} from '../../../../redux/slices/suppliersSlice'
import {
	selectAllSupplierStatistics,
	selectSupplierStatisticsStatus,
	SUPPLIERSTATS_RESET,
} from '../../../../redux/slices/supplierStatisticsSlice'
import { FetchStatus } from '../../../../types/LoadingStates'
import SupplierInsightsOverviewGraph from './Graphs/Overview'
import SupplierInsightsEmissionQualityGraph from './Graphs/QualityRating'
import SupplierInsightsScope1Graph from './Graphs/Scope1'
import SupplierInsightsScope2Graph from './Graphs/Scope2'
import SupplierInsightsScope3Graph from './Graphs/Scope3'
import useApi from '../../../../hooks/useApi'

// STILL TODO:
// 	- Convert data to intensity based percentages (even though the raw kgco2e data will be in the frontend too lol)

const CompanyInsightsSupplierTrends = () => {
	const { supplierId } = useParams()
	const dispatch = useAppDispatch()
	const api = useApi();

	const supplierEndpointStatus = useAppSelector(selectSuppliersStatus)
	const endpointStatus = useAppSelector(selectSupplierStatisticsStatus)
	const allSupplierStatistics = useAppSelector(selectAllSupplierStatistics)
	const supplierData = useAppSelector(selectIndividualSupplier)

	// Reset data
	useEffect(() => {
		dispatch({
			type: SUPPLIERS_RESET,
		})
		dispatch({
			type: SUPPLIERSTATS_RESET,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Data loading - Single Supplier and Statistics
	useEffect(() => {
		if (supplierEndpointStatus === FetchStatus.initial) {
			dispatch(getSupplier(api, supplierId))
		}
		if (endpointStatus === FetchStatus.initial) {
			if (supplierId) {
				dispatch(getSupplierPeriodStatistics(api, supplierId))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supplierEndpointStatus, endpointStatus])

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyInsightsSupplierTrends.name}</title>
			</Helmet>

			{supplierEndpointStatus === FetchStatus.loaded && (
				<PageHeadings
					crumbs={[
						{ label: routes.companyInsights.menuName as string, route: routes.companyInsights.path },
						{ label: routes.companyInsightsSuppliers.menuName as string, route: routes.companyInsightsSuppliers.path },
						{ label: supplierData?.name ?? 'Supplier' },
					]}
				/>
			)}

			{endpointStatus === FetchStatus.loading ||
				(supplierEndpointStatus === FetchStatus.loading && (
					<LoadingSpinner loadingText='Loading supplier statistics...' />
				))}

			{endpointStatus === FetchStatus.loaded && supplierEndpointStatus === FetchStatus.loaded && (
				<>
					{allSupplierStatistics.length > 0 ? (
						<TabMenu
							ariaLabel='Emission breakdowns by scope'
							additionalClass='mt-8'
							additionalPanelClass='p-4'
							tabs={[
								{
									id: 'total',
									label: 'Total Emissions by Scopes',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>
												Total Emissions by Scopes
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && (
													<SupplierInsightsOverviewGraph chartData={allSupplierStatistics} />
												)}
											</div>
										</>
									),
								},
								{
									id: 'scope-1',
									label: 'Scope 1',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>Scope 1 Breakdown</h2>
											<div className='p-4 max-w-6xl'>
												{active && (
													<SupplierInsightsScope1Graph chartData={allSupplierStatistics} />
												)}
											</div>
										</>
									),
								},
								{
									id: 'scope-2',
									label: 'Scope 2',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>Scope 2 Breakdown</h2>
											<div className='p-4 max-w-6xl'>
												{active && (
													<SupplierInsightsScope2Graph chartData={allSupplierStatistics} />
												)}
											</div>
										</>
									),
								},
								{
									id: 'scope-3',
									label: 'Scope 3 by Sector',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>
												Scope 3 Breakdown by Sector
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && (
													<SupplierInsightsScope3Graph chartData={allSupplierStatistics} />
												)}
											</div>
										</>
									),
								},
								{
									id: 'data-quality',
									label: 'Data Quality',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>
												Data Quality
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && <SupplierInsightsEmissionQualityGraph chartData={allSupplierStatistics} />}
											</div>
										</>
									),
								},
							]}
						/>
					) : (
						<div className='rounded-lg bg-charcoal-600 p-4 mt-8'>
							<p className='text-white'>
								Sorry! We couldn't find any completed accounting periods to compare
							</p>
						</div>
					)}
				</>
			)}

			{endpointStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching accounting periods associated with this organisation. If this
						problem persists, please contact the Neoni team.
					</p>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyInsightsSupplierTrends
